import { Global, Theme, css } from '@emotion/react';

import Head from 'next/head';

import { ReactNode, useEffect } from 'react';

import { ThemeType, useThemeStore } from '@stores/useThemeStore';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { BREAKPOINTS, MAX_WIDTH, media } from '@styles/css';
import { CSSProps } from '@utils/misc';

import Push, { PushSize } from './Push';

const pcLayoutCSS = (backgroundColor?: string) => (theme: Theme) =>
  css`
    position: relative;
    max-width: ${MAX_WIDTH}px;
    margin: 0 auto;
    background-color: ${backgroundColor ?? theme.var.white};
    min-height: 100%;

    color: ${theme.var.black};

    ${media.tablet} {
      max-width: ${BREAKPOINTS.tablet}px;
    }

    ${media.desktop} {
      max-width: ${BREAKPOINTS.desktop}px;
    }

    @media all and (min-width: ${BREAKPOINTS.desktop + 1}px) {
      box-shadow: 0 0 10px ${theme.var.gray200}cc;
    }

    .has-sidebar & {
      ${media.tablet} {
        max-width: none;
        margin: 0;
        box-shadow: none;
      }
    }
  `;

const mobileLayoutCSS = (theme: Theme) => css`
  position: relative;
  min-height: 100%;

  color: ${theme.var.black};
`;

type Props = {
  theme?: ThemeType;
  backgroundColor?: string;
  children?: ReactNode;
  isSSR?: boolean;
  isPush?: boolean;
};

const Layout = ({
  className,
  theme,
  backgroundColor,
  children,
  isSSR,
  isPush,
}: Props & CSSProps) => {
  const { platform } = useGlobalContext();
  const { setTheme } = useThemeStore();

  useEffect(() => {
    if (isSSR) {
      return;
    }
    // theme prop이 명시적으로 전달된 경우에만 override
    if (theme) {
      setTheme(theme);
    } else {
      // localStorage에 저장된 테마가 없을 때만 __darkMode__ 사용
      const stored = localStorage.getItem('theme');
      if (!stored) {
        setTheme(window.__darkMode__ ? 'dark' : 'light');
      }
    }
  }, [theme, isSSR]);

  return (
    <div
      className={className}
      suppressHydrationWarning
      css={
        platform === Platform.web
          ? pcLayoutCSS(backgroundColor)
          : mobileLayoutCSS
      }
    >
      {backgroundColor && (
        <Head>
          <meta name="theme-color" content={backgroundColor} />
        </Head>
      )}
      <Global
        styles={(theme: Theme) =>
          platform === Platform.web
            ? css`
                @media all and (min-width: ${BREAKPOINTS.desktop + 1}px) {
                  html {
                    background-color: ${theme.var.gray100};
                  }
                }
              `
            : css`
                html {
                  background-color: ${backgroundColor ??
                  theme.var.white} !important;
                }
              `
        }
      />

      {children}
      {isPush && <Push size={PushSize.bottomTab} bottom />}
    </div>
  );
};

export default Layout;
